<template>
  <div ref="headerSearchWrapper" :class="$style.headerSearch" @keyup.escape="closeAndFocusBack">
    <label
      :for="`aw_input_${uuid}`"
      class="awSrOnlyAbs"
      v-text="$awt('aw.common.header.user.search_input_placeholder')"
    />
    <!--
      NOTE: not using v-model because v-model is not updating during mobile
      keyboard composition. Instead the :value/@input pair is used.
      NOTE: keyup.tag can not be used because `keydown->focus->keyup(next element)`
      NOTE: keyup.enter can not be used because `click(closeBtn)->keyup.enter(input)`
    -->
    <input
      :id="`aw_input_${uuid}`"
      ref="inputElement"
      :value="searchText"
      type="text"
      role="combobox"
      autocomplete="off"
      autocorrect="on"
      spellcheck="true"
      aria-autocomplete="none"
      aria-haspopup="dialog"
      :aria-expanded="isOpened.toString()"
      :aria-controls="`aw_dialog_${uuid}`"
      :class="[$style.headerSearchInput, AnaliticsClasses.search.input, {[$style.headerSearchInputOpened]: showHeaderSearch}]"
      :placeholder="$awt('aw.common.header.user.search_input_placeholder')"
      @input="evt => searchText = evt.target.value"
      @click="showHeaderSearch = true"
      @touchstart="showHeaderSearch = true"
      @mousedown="showHeaderSearch = true"
      @keydown.shift.tab.exact="showHeaderSearch = false"
      @keydown.arrow-down.exact.prevent="openAndFocus({ doFocus: showHeaderSearch })"
      @keydown.arrow-down.alt.exact.prevent="openAndFocus({ doFocus: true })"
      @keydown.enter="suggestionClicked({ suggestionText: searchText, blurInput: $event.target, analiticsType: KS_MANUAL })"
    >
    <div :class="$style.headerSearchInputContent">
      <div
        :class="[
          $style.headerSearchInputContentButtonContainer, {
            [$style.headerSearchInputContentButtonContainerDealer]: !isShop,
            [$style.headerSearchInputContentButtonContainerDealerOpened]: showHeaderSearch && !isShop
          }]"
      >
        <aw-spinner :class="[$style.centeredRow, $style.spinner, {[$style.spinnerHidden]: !isSpinning}]" />
        <button
          v-if="!isShop"
          type="button"
          :class="['buttonReset', $style.centeredRow, AnaliticsClasses.search.barCodeIconButton]"
          :title="$awt('aw.common.header.barcode_reader')"
          @click="openBarcodeModal"
        >
          <lv-icon name="mobile-qr-code-24" :size="31" />
        </button>
        <lv-button
          v-show="showHeaderSearch"
          styling="small-secondary-dark-just-icon"
          :class="['buttonReset', $style.centeredRow, AnaliticsClasses.search.closeIconButton]"
          :title="$awt('aw.common.header.search_reset')"
          @click="closeAndResetInput"
        >
          <lv-icon name="close-24" :size="16" style="vertical-align: unset;" />
        </lv-button>
        <lv-button
          styling="small-primary-dark-just-icon"
          :class="['buttonReset', $style.centeredRow, AnaliticsClasses.search.searchIconButton, { [$style.headerSearchInactiveSearchButton]: !showHeaderSearch }]"
          :title="$awt('aw.common.header.search_icon')"
          @click="suggestionClicked({ suggestionText: searchText, analiticsType: KS_MANUAL })"
          @keydown.tab.exact="showHeaderSearch = false"
        >
          <lv-icon name="search-16" :size="16" style="vertical-align: unset;" />
        </lv-button>
      </div>
    </div>
    <!-- tabindex is needed for keyup.escape event to work on modal -->
    <div
      v-show="isOpened"
      :id="`aw_dialog_${uuid}`"
      ref="dialogWrapper"
      tabindex="-1"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="`aw_dialog_title_${uuid}`"
    >
      <div :class="$style.headerSearchDialogWrapper" :style="{left: `${-1 * dialogWrapperLeftPosition}px`}">
        <aw-spring :desktop-size="screenRange['mobile-max'] ? 100 : 10">
          <div ref="dialogContent" :class="[$style.headerSearchDialog, {[$style.headerSearchDialogDealer]: !isShop}]">
            <h2
              :id="`aw_dialog_title_${uuid}`"
              ref="dialogElement"
              tabindex="-1"
              class="awSrOnly awSrOnlyInitialW"
              style="position: absolute;"
              v-text="$awt('aw.common.header.user.search_input_dialog')"
            />
            <button
              type="button"
              :class="[$style.headerSearchDialogClose, 'awSrOnlyUntilSelected', 'awSrOnlyInitialW']"
              @click="closeAndFocusBack"
              v-text="$awt('aw.common.modal.close')"
            />
            <aw-header-search
              v-if="isOpened"
              heading-tag="h2"
              :search-text="searchText"
              @suggestion-clicked="suggestionClicked({ suggestionText: $event })"
              @search-item-selected="searchItemSelected"
              @search-start="searchIndicator = true"
              @search-end="searchEnd"
            />
          </div>
        </aw-spring>
      </div>
    </div>
  </div>
</template>

<script>
  import { defineAsyncComponent, nextTick } from 'vue';
  import { mapActions, mapState } from 'pinia';
  import { navigateTo } from 'nuxt/app';
  import { LvIcon, LvButton } from '~~/common/components/loginet-vue-shop/index.mjs';
  import { uuid4 } from '~~/common/utils';
  import urlMixin from '~~/common/mixins/urlMixin';
  import pageInstanceMixin from '~~/common/mixins/pageInstanceMixin';
  import AnaliticsClasses from '~~/common/config/AnaliticsClasses';
  import { M_DELIVERY_METHOD, M_BARCODE } from '~~/common/config/Modal.js';
  import { PUSH_KEYWORD_SEARCH, KS_MANUAL } from '~~/common/plugins/aw-analytics.js';

  import { useNotificationStore } from '~~/common/stores/notification';
  import { useModalStore } from '~~/common/stores/modal';
  import { useSearchStore } from '~~/common/stores/search';
  import { useUserInterfaceStore } from '~~/common/stores/userInterface';
  import { useProductsStore } from '~~/common/stores/products';
  import AwSpring from '~~/common/components/AwSpring';

  export default {
    name: 'AwSearchInput',
    components: {
      AwSpring,
      LvIcon,
      LvButton,
      AwHeaderSearch: defineAsyncComponent(() => import('~~/common/components/Common/Header/AwHeaderSearch.vue')),
      AwSpinner: defineAsyncComponent(() => import('~~/common/components/Common/AwSpinner')),
    },
    mixins: [urlMixin, pageInstanceMixin],
    emits: ['show-header-search'],
    data () {
      return {
        uuid: uuid4(),
        searchText: '',
        showHeaderSearch: false,
        searchIndicator: false,
        AnaliticsClasses,
        KS_MANUAL,
        dialogWrapperLeftPosition: 0,
        resizeThrottle: null,
      };
    },
    computed: {
      ...mapState(useUserInterfaceStore, {
        screenRange: state => state.mediaQueries,
      }),
      ...mapState(useNotificationStore, {
        notifications: state => state.notifications,
      }),
      ...mapState(useModalStore, {
        genericModalQueue: state => state.modals.genericModalQueue,
        isDeliveryMethodOpened: state => state.activeGenericModal.type === M_DELIVERY_METHOD,
      }),
      isSearchRequestAllow () {
        return this.searchText.length >= this.$awRuntimeConfig.searchMinLength;
      },
      isSpinning () {
        return this.isSearchRequestAllow && this.showHeaderSearch && this.searchIndicator;
      },
      isOpened () {
        return this.isSearchRequestAllow && this.showHeaderSearch;
      },
    },
    watch: {
      showHeaderSearch (newValue) {
        this.$emit('show-header-search', newValue);
        if (newValue) {
          this.addOutSideClickEventListener();
          nextTick(() => {
            this.calculateDialogWrapperLeftPosition();
          });
        }
        useUserInterfaceStore().setBackdropChildren({
          uuid: this.uuid,
          value: newValue,
        });
      },
      async searchText (newValue) {
        if (this.$awRuntimeConfig.searchLogType && !newValue.length) {
          this.searchIndicator = false;
          await this.searchesInvalidateQuery();
        }
      },
    },
    mounted () {
      this.$awEvents.addEvent('throttled-resize', this.calculateDialogWrapperLeftPosition);
    },
    beforeUnmount () {
      this.$awEvents.removeEvent('throttled-resize', this.calculateDialogWrapperLeftPosition);
      document.body.removeEventListener('click', this.hideSuggestionPanelIfOutsideClick);
      useUserInterfaceStore().setBackdropChildren({
        uuid: this.uuid,
        value: false,
      });
    },
    methods: {
      ...mapActions(useSearchStore, ['searchesLog', 'searchesInvalidateQuery']),
      openAndFocus ({ doFocus }) {
        this.showHeaderSearch = true;
        if (doFocus) {
          nextTick(() => this.$refs.dialogElement.focus());
        }
      },
      closeAndResetInput () {
        this.showHeaderSearch = false;
        this.searchText = '';
      },
      closeAndFocusBack () {
        this.$refs.inputElement.focus();
        if (this.showHeaderSearch) {
          this.showHeaderSearch = false;
        } else {
          this.searchText = '';
        }
      },
      async openBarcodeModal () {
        const modalStore = useModalStore();
        modalStore.showBarcodeModal();
        const {
          name,
          message,
        } = await this.genericModalQueue.find(({ type }) => type === M_BARCODE)?.item?.promise || {};
        if (name === 'scan') {
          this.searchText = message;
          this.suggestionClicked({
            suggestionText: this.searchText,
            analiticsType: KS_MANUAL,
          });
        }
      },
      addOutSideClickEventListener () {
        this.showHeaderSearch && nextTick(() => {
          // NOTE: remove event first, just in case
          document.body.removeEventListener('click', this.hideSuggestionPanelIfOutsideClick);
          document.body.addEventListener('click', this.hideSuggestionPanelIfOutsideClick);
        });
      },
      hideSuggestionPanelIfOutsideClick (event) {
        const targetInContent = this.$refs.dialogContent && this.$refs.dialogContent.contains(event.target);
        const tagetIsInput = !this.$refs.inputElement === event.target || this.$refs.inputElement.contains(event.target);
        if (!targetInContent && !tagetIsInput) {
          if (event.target.classList.contains('hideBasketOutsideClick') || this.isDeliveryMethodOpened) {
            return;
          }
          this.showHeaderSearch = false;
          document.body.removeEventListener('click', this.hideSuggestionPanelIfOutsideClick);
        }
      },
      searchItemSelected () {
        this.searchIndicator = false;
        if (this.isSearchRequestAllow) {
          this.showHeaderSearch = false;
          useUserInterfaceStore().setBackdropChildren({
            uuid: this.uuid,
            value: false,
          });
        }
      },
      suggestionClicked ({
        suggestionText,
        blurInput,
        analiticsType,
      }) {
        const notificationStore = useNotificationStore();
        const productsStore = useProductsStore();

        this.searchItemSelected();
        if (this.isSearchRequestAllow) {
          this.searchText = suggestionText;
          productsStore.setSearchText(suggestionText);
          productsStore.clearFilterAndSort();
          navigateTo(this.getSearchUrl(productsStore.searchText));
          this.searchesLog({
            type: 'keyword',
            keyword: suggestionText,
          });
          if (blurInput) {
            this.searchText = '';
            blurInput.blur();
          }
        } else {
          // remove older notification
          if (Object.keys(this.notifications).length) {
            for (const [key] in this.notifications) {
              notificationStore.remove(key);
            }
          }
          notificationStore.pushError({
            manualClose: false,
            text: {
              titleToken: 'aw.common.header.search.min_search_length',
            },
          });
        }
        try {
          if (analiticsType) {
            this.$awAnalytics[PUSH_KEYWORD_SEARCH]({
              analiticsType,
              searchKeyword: suggestionText,
            });
          }
        } catch (err) {
          this.$logger.error(err);
        }
      },
      calculateDialogWrapperLeftPosition () {
        this.dialogWrapperLeftPosition = this.$refs.dialogWrapper.getBoundingClientRect().left;
      },
      searchEnd () {
        this.calculateDialogWrapperLeftPosition();
        this.searchIndicator = false;
      },
    },
  };
</script>

<style module lang="scss" rel="stylesheet/scss">

.headerSearch {
  position: relative;
  display: grid;
  transition: all 0.32s ease-in-out;

  &InactiveSearchButton {
    position: absolute;
    top: 0;
    left: 0;
    color: $color-text-secondary !important;
    background: transparent !important;
    box-shadow: none !important;

    &:hover, &:focus, &:active {
      background: transparent !important;
      box-shadow: none !important;
    }

    &:focus-visible {
      box-shadow: inset 0 0 0 1px currentColor !important;
    }
  }

  &Input {
    font-size: 12px;
    line-height: 16px;
    width: 100%;
    height: inherit;
    padding: 7px 7px 7px 39px;
    color: $color-text-primary;
    border: 1px solid $color-background-2;
    border-radius: 12px;
    background: $color-background-2;
    grid-row: 1 / 2;
    grid-column: 1 / 2;
    @include tablet(min) {
      font-size: 14px;
      line-height: 20px;
    }

    &:hover:not(&Opened) {
      border-color: $color-border;
    }

    &::placeholder {
      color: $color-text-secondary;
    }

    &:focus::placeholder {
      color: transparent;
    }

    &Content {
      display: flex;
      align-self: center;
      flex-direction: row-reverse;
      justify-content: space-between;
      width: calc(100% - 16px);
      height: 0;
      transform: translateX(8px);
      grid-row: 1 / 2;
      grid-column: 1 / 2;

      &ButtonContainer {
        display: grid;
        grid-template-columns: auto 1fr 1fr;
        gap: 8px;
        justify-content: center;

        &Dealer {
          grid-template-columns: auto 1fr;

          &Opened {
            grid-template-columns: auto 1fr 1fr 1fr;

          }
        }

        .spinner {
          margin-right: 2px;
          color: $color-text-primary;

          &Hidden {
            visibility: hidden;
          }
        }
      }

    }

    &Opened {
      padding: 7px 95px 7px 15px;
      border-color: $color-border-active;
      background: $color-background-4;
    }
  }

  .centeredRow {
    translate: 0 -50%;
  }

  &Dialog {
    width: 100%;
    margin-top: 16px;
    border-radius: 16px;

    @include tablet(min) {
      @include tablet(max) {
        margin-top: 20px;
      }
    }

    &Wrapper {
      position: absolute;
      width: 100vw;

      [data-spring-container] {
        position: relative;
      }
    }

    &Close {
      position: absolute;
      top: 4px;
      right: 4px;
      padding: 4px;
      color: $color-white;
      border-radius: 8px;
      background: $color-brand-primary;
    }
  }
}
</style>
